import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Contact Page Component
 * Allows users to send messages through a contact form
 */
const Contact = () => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'contact_page'
        })
      });

      const data = await response.json();

      if (response.ok) {
        history.push('/contact/confirmation');
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    padding: '0.875rem 1rem',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: 'white'
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Spawnry</title>
        <meta
          name="description"
          content="Get in touch with the Spawnry team. We'd love to hear from you about features, feedback, or partnerships."
        />
      </Helmet>

      {/* Dark Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 4rem'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <h1 style={{
                color: 'white',
                fontWeight: '700',
                fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
                marginBottom: '0.75rem'
              }}>
                Get in Touch
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1.1rem',
                margin: 0
              }}>
                Have a question or want to discuss a partnership? We'd love to hear from you.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Contact Form */}
      <Container style={{ marginTop: '-2rem', paddingBottom: '3rem' }}>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
            }}>
              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError('')}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: '#dc2626',
                    borderRadius: '10px'
                  }}
                >
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '0.5rem' }}>
                    Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#6366f1';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '0.5rem' }}>
                    Email <span style={{ color: '#ef4444' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#6366f1';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: '500', color: '#64748b', marginBottom: '0.5rem' }}>
                    Company (optional)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#6366f1';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '0.5rem' }}>
                    Message <span style={{ color: '#ef4444' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    required
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: '120px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#6366f1';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    fontWeight: '600',
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </Form>
            </div>

            <div style={{
              textAlign: 'center',
              marginTop: '2rem',
              color: '#64748b'
            }}>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                You can also reach us directly at:
              </p>
              <a
                href="mailto:sales@spawnry.ai"
                style={{
                  color: '#6366f1',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                sales@spawnry.ai
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Contact;
